import React, { useState } from 'react'
import { CellMono } from '@/admin/shared/components/DataTable/Cell'
import Badge from '@/admin/shared/components/Badge/Badge'
import type { DataTableColumn } from '@/admin/shared/components/DataTable/DataTable'
import type { AnalyticsEvent } from '../types'
import styles from '../analytics.module.scss'

const cx = styles

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function eventBadgeVariant(eventType: string): 'primary' | 'success' | 'info' | 'purple' | 'warning' | 'muted' {
  if (eventType.startsWith('note.')) return 'primary'
  if (eventType.startsWith('user.')) return 'success'
  if (eventType.startsWith('page.')) return 'info'
  if (eventType.startsWith('auth.')) return 'purple'
  if (eventType.startsWith('error.')) return 'warning'
  return 'muted'
}

function PropertiesCell({ properties }: { properties: Record<string, unknown> | null }) {
  const [expanded, setExpanded] = useState(false)

  if (!properties || Object.keys(properties).length === 0) {
    return <span style={{ color: 'var(--t-light)' }}>—</span>
  }

  const json = JSON.stringify(properties, null, 2)
  const truncated = json.length > 40 ? json.slice(0, 40) + '…' : json

  return (
    <span>
      <button className={cx.jsonToggle} onClick={() => setExpanded(v => !v)}>
        {expanded ? '▾' : '▸'} {truncated}
      </button>
      {expanded && <div className={cx.jsonContent}>{json}</div>}
    </span>
  )
}

export const EVENT_COLUMNS: DataTableColumn<AnalyticsEvent>[] = [
  {
    key: 'event_type',
    label: 'Event Type',
    render: e => (
      <Badge variant={eventBadgeVariant(e.event_type)} dot>
        {e.event_type}
      </Badge>
    ),
  },
  {
    key: 'page_path',
    label: 'Page Path',
    render: e => <CellMono>{e.page_path ?? '—'}</CellMono>,
  },
  {
    key: 'event_target',
    label: 'Target',
    render: e => <CellMono>{e.event_target ?? '—'}</CellMono>,
  },
  {
    key: 'event_location',
    label: 'Location',
    render: e => <CellMono>{e.event_location ?? '—'}</CellMono>,
  },
  {
    key: 'event_value',
    label: 'Value',
    render: e => <CellMono>{e.event_value ?? '—'}</CellMono>,
  },
  {
    key: 'properties',
    label: 'Properties',
    render: e => <PropertiesCell properties={e.properties} />,
  },
  {
    key: 'occurred_at',
    label: 'Occurred At',
    sortable: true,
    render: e => <CellMono>{formatDateTime(e.occurred_at)}</CellMono>,
  },
]

